import React, { FC } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { GIS_NO_ORDER_UID } from "@app/utils/gisEntrySource";
import {
  IntakeStepActiveDisease,
  IntakeStepReason,
  IntakeStepUnderstanding,
  IntakeStepPreference,
  IntakeStepDoctorAppointment,
  IntakeStepLocationInterpreter,
  IntakeStepConsent,
  IntakeStepSelectTest,
  IntakeStepCancerType,
  IntakeStepCancerStage,
  IntakeStepClinicInfo,
} from "@app/pages/private/scheduleAGeneticInformationSession/steps";
import GuestIntakeConfirmInfo from "./GuestIntakeConfirmInfo";
import GuestNoOrderConfirmInfo from "./GuestNoOrderConfirmInfo";

/**
 * Confirm-info step (step 1). Renders the no-order variant (the Figma
 * "fill out this form to schedule a session" screen) when the funnel routed a
 * matched-but-no-eligible-order patient in with the `GIS_NO_ORDER_UID`
 * sentinel; otherwise the order-backed contact-confirmation variant.
 */
const GuestConfirmInfoStep: FC = () => {
  const { orderUidPublic } = useParams<{ orderUidPublic: string }>();
  return orderUidPublic === GIS_NO_ORDER_UID ? (
    <GuestNoOrderConfirmInfo />
  ) : (
    <GuestIntakeConfirmInfo />
  );
};

/**
 * Router for the anonymous order-lookup (Public_Entry_Order_Lookup) ONC intake
 * flow. It reuses the authenticated intake step components unchanged — they are
 * driven entirely by `IntakeFormContext` and resolve their navigation against
 * the same route paths, which are mounted in the public router under the guest
 * layout — and only swaps in the guest-specific confirm-info step (which uses
 * token-authorized prefill/persistence instead of the authenticated providers).
 *
 * A guest matched to a single order skips the no-order-only steps. A no-order
 * guest (matched patient, no GIS-eligible order) instead runs the extended
 * sequence, so clinic-info / select-test / cancer-type / cancer-stage are
 * mounted here too and gated to the no-order flow by the active step sequence.
 */
const GuestIntakeFormRouter: FC = () => {
  return (
    <Routes>
      {/* Step 1: Confirm info (order-backed or no-order guest variant) */}
      <Route path="/" element={<GuestConfirmInfoStep />} />

      {/* No-order only: ordering physician and clinic */}
      <Route path="/clinic-info" element={<IntakeStepClinicInfo />} />

      {/* No-order only: pick the test to discuss */}
      <Route path="/select-test" element={<IntakeStepSelectTest />} />

      {/* No-order only: cancer type */}
      <Route path="/cancer-type" element={<IntakeStepCancerType />} />

      {/* No-order only: cancer stage */}
      <Route path="/cancer-stage" element={<IntakeStepCancerStage />} />

      {/* Step 2: Reason for appointment */}
      <Route path="/reason" element={<IntakeStepReason />} />

      {/* Step 3: Active disease or remission */}
      <Route path="/active-disease" element={<IntakeStepActiveDisease />} />

      {/* Step 4: Ease of understanding */}
      <Route path="/understanding" element={<IntakeStepUnderstanding />} />

      {/* Step 5: Preference - read first or talk */}
      <Route path="/preference" element={<IntakeStepPreference />} />

      {/* Step 6: Doctor appointment scheduled */}
      <Route
        path="/doctor-appointment"
        element={<IntakeStepDoctorAppointment />}
      />

      {/* Step 7: Location & interpreter preference */}
      <Route
        path="/location-interpreter"
        element={<IntakeStepLocationInterpreter />}
      />

      {/* Step 8: Consent confirmation (navigates to the custom scheduler) */}
      <Route path="/consent" element={<IntakeStepConsent />} />

      {/* Fallback: redirect to first step */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default GuestIntakeFormRouter;
